// Plantillas de CV optimizadas para ATS: una sola columna, tipografías
// estándar empaquetadas con la app (Libertinus / New Computer Modern),
// sin tablas ni gráficos, secciones con nombres canónicos y texto 100%
// extraíble — lo que los parsers de candidaturas saben leer.
//
// TODO el contenido vive en un bloque `#let cv = (…)` al principio (CV_LET);
// el cuerpo (BODY) lo renderiza con bucles `#for`. El formulario visual edita
// ese bloque —incluidas altas/bajas de líneas— sin tocar el render. Las
// plantillas sólo se diferencian en el estilo (fuentes/márgenes/encabezados)
// y en la cabecera.

import { DEFAULT_CV, serializeCv, parseCv, applyCv } from "./cv-data";

export interface Template {
  id: string;
  name: string;
  description: string;
  source: string;
}

// Bloque de datos por defecto (canónico) — compartido por todas las plantillas.
const CV_LET = serializeCv(DEFAULT_CV);

// Render compartido del cuerpo. Encabezados canónicos literales (los lee el
// ATS); el contenido sale del bloque `cv` vía bucles.
const BODY = `== Perfil

#cv.resumen

== Experiencia

#for e in cv.experiencia [
  #grid(columns: (1fr, auto), [*#e.puesto* — #e.empresa], [#e.fechas])
  #for p in e.puntos [
    - #p
  ]
]

== Formación

#for f in cv.formacion [
  #grid(columns: (1fr, auto), [*#f.titulo* — #f.centro], [#f.fechas])
]

== Certificaciones

#for c in cv.certificaciones [
  - #c
]

== Habilidades

#for h in cv.habilidades [
  - *#h.etiqueta:* #h.valor
]
`;

const CLASICO = `${CV_LET}

#set document(author: cv.perfil.nombre, title: "CV — " + cv.perfil.nombre)
#set page(paper: "a4", margin: (x: 1.6cm, y: 1.5cm))
#set text(font: "Libertinus Serif", size: 10.5pt, lang: "es")
#set par(justify: true, leading: 0.6em)

#show heading.where(level: 2): it => block(above: 1.1em, below: 0.6em)[
  #text(size: 11pt, weight: "bold", upper(it.body))
  #v(-0.55em)
  #line(length: 100%, stroke: 0.6pt)
]

#align(center)[
  #text(size: 20pt, weight: "bold")[#cv.perfil.nombre]

  #text(size: 11pt)[#cv.perfil.titular]

  #text(size: 10pt)[
    #cv.perfil.ubicacion ·
    #cv.perfil.telefono ·
    #link("mailto:" + cv.perfil.email)[#cv.perfil.email] ·
    #link("https://" + cv.perfil.linkedin)[#cv.perfil.linkedin] ·
    #link("https://" + cv.perfil.github)[#cv.perfil.github]
  ]
]

${BODY}`;

const COMPACTO = `${CV_LET}

#set document(author: cv.perfil.nombre, title: "CV — " + cv.perfil.nombre)
#set page(paper: "a4", margin: (x: 1.4cm, y: 1.2cm))
#set text(font: "New Computer Modern", size: 10pt, lang: "es")
#set par(justify: true, leading: 0.55em)
#set list(spacing: 0.55em)

#show heading.where(level: 2): it => block(above: 0.9em, below: 0.45em)[
  #text(size: 10.5pt, weight: "bold", upper(it.body))
  #v(-0.5em)
  #line(length: 100%, stroke: 0.5pt)
]

#text(size: 17pt, weight: "bold")[#cv.perfil.nombre]
#h(1fr)
#text(size: 9.5pt)[
  #cv.perfil.telefono · #link("mailto:" + cv.perfil.email)[#cv.perfil.email]
]

#text(size: 10pt)[#cv.perfil.titular]

#text(size: 9.5pt)[
  #cv.perfil.ubicacion ·
  #link("https://" + cv.perfil.linkedin)[#cv.perfil.linkedin] ·
  #link("https://" + cv.perfil.github)[#cv.perfil.github]
]

${BODY}`;

// ── Plantilla con FOTO ──────────────────────────────────────────────────
// Usa image("/profile.png"), la ruta donde la app monta la foto de perfil
// (o un placeholder si aún no se ha subido ninguna).
const CON_FOTO = `${CV_LET}

#set document(author: cv.perfil.nombre, title: "CV — " + cv.perfil.nombre)
#set page(paper: "a4", margin: (x: 1.5cm, y: 1.4cm))
#set text(font: "Libertinus Serif", size: 10.5pt, lang: "es")
#set par(justify: true, leading: 0.6em)

#show heading.where(level: 2): it => block(above: 1.1em, below: 0.6em)[
  #text(size: 11pt, weight: "bold", upper(it.body))
  #v(-0.55em)
  #line(length: 100%, stroke: 0.6pt)
]

#grid(
  columns: (2.6cm, 1fr),
  column-gutter: 0.8cm,
  align: (center + horizon, left + horizon),
  box(clip: true, radius: 50%, width: 2.4cm, height: 2.4cm,
    image("/profile.png", width: 2.4cm, height: 2.4cm, fit: "cover")),
  [
    #text(size: 20pt, weight: "bold")[#cv.perfil.nombre]

    #text(size: 10pt)[
      #cv.perfil.titular · #cv.perfil.ubicacion \\
      #cv.perfil.telefono ·
      #link("mailto:" + cv.perfil.email)[#cv.perfil.email] \\
      #link("https://" + cv.perfil.linkedin)[#cv.perfil.linkedin] ·
      #link("https://" + cv.perfil.github)[#cv.perfil.github]
    ]
  ],
)

${BODY}`;

// ── Plantilla MONO (estética terminal) ──────────────────────────────────
const MONO = `${CV_LET}

#set document(author: cv.perfil.nombre, title: "CV — " + cv.perfil.nombre)
#set page(paper: "a4", margin: (x: 1.5cm, y: 1.4cm))
#set text(font: "DejaVu Sans Mono", size: 9pt, lang: "es")
#set par(leading: 0.65em)

#show heading.where(level: 2): it => block(above: 1em, below: 0.5em)[
  #text(weight: "bold")[#upper(it.body)]
  #v(-0.5em)
  #line(length: 100%, stroke: 0.5pt + gray)
]

#text(size: 16pt, weight: "bold")[#cv.perfil.nombre] \\
#text(size: 9pt)[
  #cv.perfil.titular \\
  #link("mailto:" + cv.perfil.email)[#cv.perfil.email] · #cv.perfil.telefono \\
  #link("https://" + cv.perfil.linkedin)[#cv.perfil.linkedin] ·
  #link("https://" + cv.perfil.github)[#cv.perfil.github]
]

${BODY}`;

// ── Plantilla EJECUTIVA (serif, dos niveles de titular) ─────────────────
const EJECUTIVO = `${CV_LET}

#set document(author: cv.perfil.nombre, title: "CV — " + cv.perfil.nombre)
#set page(paper: "a4", margin: (x: 1.8cm, y: 1.6cm))
#set text(font: "New Computer Modern", size: 10.5pt, lang: "es")
#set par(justify: true, leading: 0.62em)

#show heading.where(level: 2): it => block(above: 1.1em, below: 0.55em)[
  #text(size: 12pt, weight: "bold", fill: rgb("#8a1414"))[#it.body]
]

#align(center)[
  #text(size: 24pt, weight: "bold")[#cv.perfil.nombre]

  #text(size: 12pt, fill: rgb("#555555"))[#cv.perfil.titular]

  #text(size: 9.5pt)[
    #cv.perfil.ubicacion · #cv.perfil.telefono ·
    #link("mailto:" + cv.perfil.email)[#cv.perfil.email] ·
    #link("https://" + cv.perfil.linkedin)[#cv.perfil.linkedin] ·
    #link("https://" + cv.perfil.github)[#cv.perfil.github]
  ]
]

#line(length: 100%, stroke: 0.5pt)

${BODY}`;

export const TEMPLATES: Template[] = [
  {
    id: "clasico",
    name: "clásico",
    description: "Serif clásica, secciones completas (perfil, experiencia, certificaciones).",
    source: CLASICO,
  },
  {
    id: "compacto",
    name: "compacto",
    description: "Denso, pensado para una sola página.",
    source: COMPACTO,
  },
  {
    id: "con-foto",
    name: "con foto",
    description: "Cabecera con foto de perfil circular (usa el botón «foto»).",
    source: CON_FOTO,
  },
  {
    id: "mono",
    name: "mono",
    description: "Estética terminal en monospace, encabezados con prompt.",
    source: MONO,
  },
  {
    id: "ejecutivo",
    name: "ejecutivo",
    description: "Serif elegante, nombre grande + rol, acento rojo.",
    source: EJECUTIVO,
  },
];

// Templates that reference the profile image — the UI can hint to upload one.
export const PHOTO_TEMPLATE_IDS = new Set(["con-foto"]);

export const DEFAULT_TEMPLATE = TEMPLATES[0];

/**
 * The default template the source currently corresponds to, or null when the
 * source is a custom design. A source "matches" a template when it differs
 * from it ONLY in the `#let cv` data block — i.e. it was produced by the
 * visual form, not by editing the body. Drives the two-mode UI.
 */
export function matchingTemplate(source: string): Template | null {
  const cv = parseCv(source);
  if (!cv) return null;
  for (const t of TEMPLATES) {
    if (applyCv(t.source, cv) === source) return t;
  }
  return null;
}

/** Whether the photo control should be offered: photo template, or custom. */
export function allowsPhoto(source: string): boolean {
  const t = matchingTemplate(source);
  return t ? PHOTO_TEMPLATE_IDS.has(t.id) : true;
}
